import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChartBarIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { ConfirmModal } from '../../../ui/atoms';
import Placeholder from '../../../ui/atoms/Placeholder';
import InfoTip from '../../../ui/atoms/InfoTip';
import { cn } from '../../../ui/cn';
import AnalysisTagList from './AnalysisTagList';
import {
  formatClassificationLabel,
  formatIntentLabel,
  formatResolutionStatusLabel,
  formatSentimentLabel,
  formatTagLabel,
  getRecommendedActionLabel,
  getSessionActionLabel,
  getSystemActionLabel,
  getPriorityReasonLabel,
  getPriorityBands,
  getEffortBands,
  getConfidenceBands
} from '../labels/sessionLabels';
import { parseJsonField } from '../utils/parseJsonField';
import { buildTrajectory } from '../utils/buildTrajectory';

const PANEL_CLASS = 'w-80 flex-shrink-0 min-h-0 border-l border-neutral-200 bg-neutral-white px-4 py-4 overflow-y-auto thin-scrollbar';

const BADGE_VARIANTS = {
  emerald: 'bg-emerald-50 text-emerald-700',
  green: 'bg-green-50 text-green-700',
  blue: 'bg-blue-50 text-blue-700',
  purple: 'bg-purple-50 text-purple-700',
  orange: 'bg-orange-50 text-orange-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
  yellow: 'bg-yellow-50 text-yellow-700',
  gray: 'bg-gray-100 text-gray-600',
  muted: 'bg-gray-50 text-gray-500'
};

const toEvidenceList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'object') return [value];
  return [];
};

const toBool = (value) => value === true || value === 'true';

const EvidenceQuote = ({ evidence }) => {
  if (!evidence?.quote) return null;

  return (
    <div className="mt-2 flex items-start gap-1.5 rounded-md border-l-2 border-neutral-300 bg-neutral-50 px-2.5 py-2">
      {evidence.turn_id && (
        <span className="mt-0.5 flex-shrink-0 font-mono text-[10px] text-gray-400">T{evidence.turn_id}</span>
      )}
      <span className="text-[11px] italic leading-relaxed text-gray-500">"{evidence.quote}"</span>
    </div>
  );
};

const Badge = ({ children, variant = 'gray', border = false, className = '' }) => {
  const borderCls = border
    ? {
        emerald: 'border border-emerald-200',
        red: 'border border-red-200',
        orange: 'border border-orange-200',
        amber: 'border border-amber-200'
      }[variant] || ''
    : '';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
        BADGE_VARIANTS[variant] || BADGE_VARIANTS.gray,
        borderCls,
        className
      )}
    >
      {children}
    </span>
  );
};

const SegmentBar = ({ bands, activeKey }) => {
  const activeIdx = bands.findIndex((band) => band.key === activeKey);
  const segWidth = 100 / bands.length;
  const markerLeft = activeIdx >= 0 ? segWidth * activeIdx + segWidth / 2 : 0;

  return (
    <div className="mt-1.5">
      <div className="relative">
        <div className="flex h-[6px] w-full overflow-hidden rounded-full">
          {bands.map((band, index) => (
            <div
              key={band.key}
              className="flex-1 transition-opacity"
              style={{
                backgroundColor: band.color,
                opacity: activeIdx >= 0 && index !== activeIdx ? 0.25 : 1
              }}
            />
          ))}
        </div>
        {activeIdx >= 0 && (
          <div
            className="absolute -top-[3px]"
            style={{ left: `${markerLeft}%`, transform: 'translateX(-50%)' }}
          >
            <div
              className="h-3 w-3 rounded-full border-2 border-white shadow-sm"
              style={{ backgroundColor: bands[activeIdx].color }}
            />
          </div>
        )}
      </div>
      <div className="mt-1 flex justify-between">
        {bands.map((band, index) => (
          <span
            key={band.key}
            className={cn(
              'flex-1 text-center text-[10px]',
              index === activeIdx ? 'font-semibold text-gray-900' : 'text-gray-400'
            )}
          >
            {band.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const PanelSection = ({ title, tip, children, className }) => (
  <section className={cn('py-4', className)}>
    {title && (
      <h3 className="mb-3 flex items-center text-xs font-semibold text-gray-500">
        {title}
        {tip && <InfoTip text={tip} />}
      </h3>
    )}
    {children}
  </section>
);

const DetailRows = ({ children }) => <div className="space-y-3">{children}</div>;

const DetailRow = ({ label, value, children }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="shrink-0 text-xs font-medium text-gray-600">{label}</span>
    <div className="min-w-0 text-right text-xs text-gray-900">{children ?? value}</div>
  </div>
);

const DetailBadgeRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="shrink-0 text-xs font-medium text-gray-600">{label}</span>
    <div className="flex flex-wrap justify-end gap-1">{children}</div>
  </div>
);

const ConfidenceBar = ({ value, t }) => {
  const pct = Math.max(0, Math.min(100, (Number(value) || 0) * 100));
  const level = pct >= 80 ? 'high' : pct >= 60 ? 'medium' : 'low';

  return (
    <>
      <SegmentBar bands={getConfidenceBands(t)} activeKey={level} />
      <p className="mt-2 text-xs leading-relaxed text-gray-600">
        {t('assistant.confidence.score', { value: pct.toFixed(0) })}
      </p>
    </>
  );
};

const ChatDetailsPanel = ({
  sessionId,
  chatMetadata,
  messages = [],
  isOwner = true,
  sessionsLoading = false,
  sessionLoading = false,
  onArchive
}) => {
  const { t } = useTranslation();
  const [isArchiveModalOpen, setIsArchiveModalOpen] = React.useState(false);
  const [isArchiveSubmitting, setIsArchiveSubmitting] = React.useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = React.useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return t('assistant.sessionDetails.notAvailable');

    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');

      return `${day} ${month} ${year}, ${hours}:${minutes}:${seconds}`;
    } catch {
      return t('assistant.sessionDetails.notAvailable');
    }
  };

  if (!sessionId) {
    return (
      <div className={PANEL_CLASS}>
        {sessionsLoading ? (
          <Placeholder size="s" className="h-full min-h-[240px] border-0 shadow-none" />
        ) : (
          <Placeholder
            empty
            size="s"
            icon={ChartBarIcon}
            message={t('assistant.sessionDetails.title')}
            description={t('assistant.analysis.selectConversation')}
            className="h-full min-h-[240px]"
          />
        )}
      </div>
    );
  }

  if (sessionLoading) {
    return (
      <div className={PANEL_CLASS}>
        <Placeholder size="m" className="h-full min-h-[240px] border-0 shadow-none" />
      </div>
    );
  }

  const rationale = parseJsonField(chatMetadata?.overall_sentiment_rationale);
  const topics = parseJsonField(chatMetadata?.overall_sentiment_topics);
  const resolvedIntent = chatMetadata?.primary_intent || topics?.primary_intent || null;
  const resolvedStatus = chatMetadata?.intent_fulfilled
    ? (chatMetadata.intent_fulfilled === 'true'
      ? 'resolved'
      : (chatMetadata.intent_fulfilled === 'partial' ? 'partially_resolved' : 'unresolved'))
    : (chatMetadata?.resolution_status || null);
  const resolvedPriority = chatMetadata?.priority_band || chatMetadata?.business_risk || null;
  const resolvedEffort = chatMetadata?.effort_score ?? chatMetadata?.intensity_score ?? null;
  const resolvedLoopDetected = (chatMetadata?.no_progress_loop ?? chatMetadata?.loop_detected) || false;
  const resolvedLoopCount = chatMetadata?.loop_count || 0;
  const resolvedRecommendedAction =
    chatMetadata?.recommended_action
    || (typeof rationale?.recommended_action === 'string' ? rationale.recommended_action : null);
  const resolvedTags = Array.isArray(chatMetadata?.tags)
    ? chatMetadata.tags
    : (Array.isArray(rationale?.tags)
      ? rationale.tags
      : (Array.isArray(chatMetadata?.judge_output?.tags) ? chatMetadata.judge_output.tags : []));
  const normalizedTopicSet = new Set(
    [...(Array.isArray(topics?.topics) ? topics.topics : []), resolvedIntent]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase())
  );
  const filteredTags = resolvedTags.filter((tag) => !normalizedTopicSet.has(String(tag).trim().toLowerCase()));
  const displayTags = filteredTags.length > 0 ? filteredTags : resolvedTags;
  const resolvedFailedRecovery = toBool(chatMetadata?.failed_recovery ?? rationale?.failed_recovery);
  const resolvedUnsupportedPromise = toBool(chatMetadata?.unsupported_promise ?? rationale?.unsupported_promise);
  const turningPointSource =
    chatMetadata?.turning_point
    || rationale?.turning_point
    || chatMetadata?.judge_output?.turning_point
    || null;
  const turningPointFromColumns = (
    chatMetadata?.turning_point_turn_id != null
    || chatMetadata?.turning_point_direction
    || chatMetadata?.turning_point_trigger
  )
    ? {
      turn_id: chatMetadata?.turning_point_turn_id ?? null,
      direction: chatMetadata?.turning_point_direction ?? null,
      trigger: chatMetadata?.turning_point_trigger ?? null,
      evidence: []
    }
    : null;
  const turningPointCandidate = turningPointSource || turningPointFromColumns;
  const resolvedTurningPoint = turningPointCandidate && typeof turningPointCandidate === 'object'
    ? {
      turn_id: turningPointCandidate.turn_id ?? null,
      direction: turningPointCandidate.direction ?? null,
      trigger: turningPointCandidate.trigger ?? null,
      evidence: toEvidenceList(turningPointCandidate.evidence)
    }
    : null;
  const hasTurningPointDetails = Boolean(
    resolvedTurningPoint
    && (resolvedTurningPoint.turn_id != null
      || resolvedTurningPoint.direction
      || (resolvedTurningPoint.trigger && String(resolvedTurningPoint.trigger).trim()))
  );
  const intentEvidence = toEvidenceList(rationale?.intent_fulfilled_evidence || chatMetadata?.intent_fulfilled_evidence);
  const resolvedSessionActions = Array.isArray(chatMetadata?.session_action)
    ? chatMetadata.session_action
    : (Array.isArray(rationale?.session_action) ? rationale.session_action : []);
  const resolvedSystemAction =
    (typeof chatMetadata?.system_action === 'string' ? chatMetadata.system_action : null)
    || (typeof rationale?.system_action === 'string' ? rationale.system_action : null);
  const actionCopyLabel = resolvedRecommendedAction
    ? getRecommendedActionLabel(resolvedRecommendedAction, t)
    : null;
  const sessionActionText = resolvedSessionActions.length > 0
    ? resolvedSessionActions.map((value) => getSessionActionLabel(value, t)).join(', ')
    : '';
  const systemActionText = resolvedSystemAction
    ? getSystemActionLabel(resolvedSystemAction, t)
    : '';
  const hasTwoLayerActions = Boolean(sessionActionText || systemActionText);
  const recommendedActionLabel = actionCopyLabel || (resolvedRecommendedAction ? getRecommendedActionLabel(resolvedRecommendedAction, t) : null);
  const summaryText =
    chatMetadata?.session_summary
    || chatMetadata?.judge_output?.session_summary
    || chatMetadata?.title
    || '';
  const summaryLimit = 260;
  const canExpandSummary = summaryText.length > summaryLimit;
  const visibleSummary = canExpandSummary && !isSummaryExpanded
    ? `${summaryText.slice(0, summaryLimit).trimEnd()}...`
    : summaryText;
  const liveMessageCount = messages.length;
  const analyzedMessageCount = Number(chatMetadata?.messages_count) || 0;
  const displayMessageCount = liveMessageCount > 0 ? liveMessageCount : analyzedMessageCount;
  const hasNewMessagesSinceAnalysis =
    analyzedMessageCount > 0 && liveMessageCount > analyzedMessageCount;

  const trajectory = buildTrajectory(chatMetadata, messages, rationale);
  const trajectoryPoints = trajectory.message_valences.map((mv, idx) => ({
    idx: idx + 1,
    label: `Msg ${idx + 1}`,
    role: mv.role || 'unknown',
    valence: mv.valence
  }));
  const businessRiskRationale =
    (typeof rationale?.business_risk_rationale === 'string' ? rationale.business_risk_rationale.trim() : '')
    || (typeof chatMetadata?.business_risk_rationale === 'string' ? chatMetadata.business_risk_rationale.trim() : '');
  const businessRiskEvidence =
    rationale?.business_risk_evidence && typeof rationale.business_risk_evidence === 'object'
      ? rationale.business_risk_evidence
      : null;
  const priorityReasonCode =
    (typeof rationale?.priority_reason_code === 'string' ? rationale.priority_reason_code.trim() : '')
    || (typeof chatMetadata?.priority_reason_code === 'string' ? chatMetadata.priority_reason_code.trim() : '');
  const resolutionVariant = {
    resolved: 'emerald',
    partially_resolved: 'orange',
    unresolved: 'red'
  };
  const hasRiskFlags =
    chatMetadata?.exit_intent
    || (chatMetadata?.closing_missing && resolvedStatus !== 'resolved')
    || resolvedLoopDetected;

  const handleArchive = async () => {
    if (!onArchive || !sessionId) return;

    try {
      setIsArchiveSubmitting(true);
      await onArchive(sessionId);
      setIsArchiveModalOpen(false);
    } finally {
      setIsArchiveSubmitting(false);
    }
  };

  return (
    <>
      <div className={PANEL_CLASS}>
        <div className="divide-y divide-neutral-200">
          <PanelSection title={t('assistant.sessionDetails.title')}>
            <DetailRows>
              <DetailRow label={t('assistant.sessionDetails.type')} value={chatMetadata?.source || t('assistant.sessionDetails.userChat')} />
              <DetailRow label={t('assistant.sessionDetails.createdAt')} value={formatDate(chatMetadata?.date_created)} />
              {displayMessageCount > 0 && (
                <DetailRow
                  label={t('assistant.sessionDetails.messages')}
                  value={
                    hasNewMessagesSinceAnalysis
                      ? `${displayMessageCount} (${t('assistant.sessionDetails.analyzedCount', { count: analyzedMessageCount })})`
                      : displayMessageCount
                  }
                />
              )}
              {chatMetadata?.linked_widget && (
                <DetailRow label={t('assistant.sessionDetails.widget')}>
                  <a href={`/ai-agents/${chatMetadata.linked_widget}`} target="_blank" rel="noopener noreferrer">
                    <Badge variant="blue" className="cursor-pointer transition-colors hover:bg-blue-100">
                      {t('assistant.analysis.viewWidget')}
                    </Badge>
                  </a>
                </DetailRow>
              )}
              {chatMetadata?.linkedlead && (
                <DetailRow label={t('assistant.analysis.lead')}>
                  <a href={`/crm/${chatMetadata.linkedlead}`} target="_blank" rel="noopener noreferrer">
                    <Badge variant="emerald" className="cursor-pointer transition-colors hover:bg-emerald-100">
                      {t('assistant.analysis.viewLead')}
                    </Badge>
                  </a>
                </DetailRow>
              )}
              {resolvedIntent && (
                <DetailRow label={t('assistant.analysis.intent')} value={formatIntentLabel(resolvedIntent, t)} />
              )}
              {hasRiskFlags && (
                <>
                  <DetailBadgeRow label={t('assistant.analysis.risk')}>
                    {chatMetadata.exit_intent && <Badge variant="red" border>{t('assistant.analysis.badges.exitIntent')}</Badge>}
                    {chatMetadata.closing_missing && resolvedStatus !== 'resolved' && (
                      <Badge variant="orange" border>{t('assistant.analysis.badges.noClosingReply')}</Badge>
                    )}
                    {resolvedLoopDetected && (
                      <Badge variant="amber" border>
                        {resolvedLoopCount
                          ? t('assistant.analysis.badges.loopCount', { count: resolvedLoopCount })
                          : t('assistant.analysis.badges.loop')}
                      </Badge>
                    )}
                  </DetailBadgeRow>
                  {chatMetadata.exit_intent && rationale?.exit_intent_evidence?.quote && (
                    <EvidenceQuote evidence={rationale.exit_intent_evidence} />
                  )}
                </>
              )}
              {(resolvedFailedRecovery || resolvedUnsupportedPromise) && (
                <DetailBadgeRow label={t('assistant.analysis.signals')}>
                  {resolvedFailedRecovery && <Badge variant="red" border>{t('assistant.analysis.badges.failedRecovery')}</Badge>}
                  {resolvedUnsupportedPromise && <Badge variant="orange" border>{t('assistant.analysis.badges.unsupportedPromise')}</Badge>}
                </DetailBadgeRow>
              )}
              {chatMetadata?.overall_sentiment && chatMetadata.overall_sentiment !== 'neutral' && (
                <DetailRow
                  label={(
                    <span className="inline-flex items-center">
                      {t('assistant.analysis.sentiment')}
                      <InfoTip text={t('assistant.analysis.sentimentTip')} />
                    </span>
                  )}
                  value={formatSentimentLabel(chatMetadata.overall_sentiment, t)}
                />
              )}
              {resolvedStatus && (
                <DetailRow label={t('assistant.analysis.status')}>
                  <Badge variant={resolutionVariant[resolvedStatus] || 'red'}>
                    {formatResolutionStatusLabel(resolvedStatus, t)}
                  </Badge>
                </DetailRow>
              )}
            </DetailRows>

            {onArchive && isOwner && (
              <button
                type="button"
                onClick={() => setIsArchiveModalOpen(true)}
                disabled={isArchiveSubmitting}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-2 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2"
              >
                <ArchiveBoxIcon className="h-4 w-4" />
                <span>{t('assistant.sessionDetails.archiveSession')}</span>
              </button>
            )}
          </PanelSection>

          {summaryText && (
            <PanelSection title={t('assistant.analysis.summary')}>
              <p className="text-xs leading-relaxed text-gray-900">{visibleSummary}</p>
              {canExpandSummary && (
                <button
                  type="button"
                  onClick={() => setIsSummaryExpanded((prev) => !prev)}
                  className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  {isSummaryExpanded ? t('assistant.analysis.collapse') : t('assistant.analysis.expand')}
                </button>
              )}
            </PanelSection>
          )}

          {chatMetadata?.overall_sentiment ? (
            <>
              {topics?.topics?.length > 0 && (
                <PanelSection title={t('assistant.analysis.classification')}>
                  <AnalysisTagList
                    tags={topics.topics}
                    t={t}
                    formatLabel={formatClassificationLabel}
                  />
                </PanelSection>
              )}

              {(recommendedActionLabel || hasTwoLayerActions) && (
                <PanelSection title={t('assistant.analysis.recommendedAction')}>
                  {!hasTwoLayerActions && recommendedActionLabel && (
                    <p className="text-xs font-medium leading-relaxed text-gray-900">{recommendedActionLabel}</p>
                  )}
                  {hasTwoLayerActions && (
                    <div className="space-y-1.5">
                      <p className="text-xs leading-relaxed text-gray-600">{t('assistant.analysis.recommendedActionHint')}</p>
                      {sessionActionText && (
                        <p className="text-xs leading-relaxed text-gray-700">
                          <span className="font-medium text-gray-900">{t('assistant.analysis.now')}:</span> {sessionActionText}
                        </p>
                      )}
                      {systemActionText && (
                        <p className="text-xs leading-relaxed text-gray-700">
                          <span className="font-medium text-gray-900">{t('assistant.analysis.system')}:</span> {systemActionText}
                        </p>
                      )}
                    </div>
                  )}
                </PanelSection>
              )}

              {chatMetadata.confidence != null && chatMetadata.confidence > 0 && (
                <PanelSection title={t('assistant.confidence.title')}>
                  <ConfidenceBar value={chatMetadata.confidence} t={t} />
                </PanelSection>
              )}

              {resolvedPriority && (
                <PanelSection title={t('assistant.analysis.priority')} tip={t('assistant.analysis.priorityTip')}>
                  <SegmentBar bands={getPriorityBands(t)} activeKey={resolvedPriority} />
                  {(businessRiskRationale || priorityReasonCode) && (
                    <p className="mt-2 text-xs leading-relaxed text-gray-600">
                      {businessRiskRationale || t('assistant.analysis.reason', { reason: getPriorityReasonLabel(priorityReasonCode, t) })}
                    </p>
                  )}
                  <EvidenceQuote evidence={businessRiskEvidence} />
                </PanelSection>
              )}

              {(rationale?.intent_fulfilled_rationale || intentEvidence.length > 0) && (
                <PanelSection title={t('assistant.analysis.intentOutcome')}>
                  {rationale?.intent_fulfilled_rationale && (
                    <p className="text-xs leading-relaxed text-gray-600">{rationale.intent_fulfilled_rationale}</p>
                  )}
                  {intentEvidence.slice(0, 2).map((evidence, idx) => (
                    <EvidenceQuote key={idx} evidence={evidence} />
                  ))}
                </PanelSection>
              )}

              {resolvedEffort != null && resolvedEffort !== 0 && (
                <PanelSection title={t('assistant.analysis.effort')} tip={t('assistant.analysis.effortTip')}>
                  <SegmentBar bands={getEffortBands(t)} activeKey={String(resolvedEffort)} />
                  {rationale?.effort_rationale && (
                    <p className="mt-2 text-xs leading-relaxed text-gray-600">{rationale.effort_rationale}</p>
                  )}
                  <EvidenceQuote evidence={rationale?.effort_evidence} />
                </PanelSection>
              )}

              {trajectoryPoints.length > 0 && (
                <PanelSection title={t('assistant.analysis.sentimentTrend')} tip={t('assistant.analysis.sentimentTrendTip')}>
                  <div className="mb-2 rounded-lg border border-neutral-200 bg-white p-3">
                    <div style={{ width: '100%', height: 160 }}>
                      <ResponsiveContainer>
                        <LineChart data={trajectoryPoints} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
                          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6b7280' }} minTickGap={12} />
                          <YAxis
                            domain={[-1, 1]}
                            ticks={[-1, -0.5, 0, 0.5, 1]}
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickFormatter={(value) => Number(value).toFixed(1)}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1f2937',
                              border: '1px solid #374151',
                              borderRadius: '8px',
                              color: '#f9fafb',
                              fontSize: '11px'
                            }}
                            labelStyle={{ color: '#f9fafb', fontWeight: 600 }}
                            formatter={(value) => [t('assistant.analysis.sentimentTooltip', { value: Number(value).toFixed(2) }), '']}
                            labelFormatter={(label, payload) => {
                              const point = payload?.[0]?.payload;
                              return `${label} (${point?.role || 'unknown'})`;
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="valence"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#3b82f6', stroke: '#fff', strokeWidth: 1.5 }}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-[9px] text-neutral-500">
                    <span>
                      {hasNewMessagesSinceAnalysis
                        ? t('assistant.analysis.messagesAnalyzedStale', {
                          analyzed: trajectoryPoints.length,
                          total: liveMessageCount
                        })
                        : t('assistant.analysis.messagesAnalyzed', { count: trajectoryPoints.length })}
                    </span>
                    <span className="text-neutral-400">
                      {t('assistant.analysis.trajectoryStart', { value: trajectory.opening_valence != null ? trajectory.opening_valence.toFixed(2) : '0.00' })} →
                      {t('assistant.analysis.trajectoryEnd', { value: trajectory.closing_valence != null ? trajectory.closing_valence.toFixed(2) : '0.00' })}
                    </span>
                  </div>
                  {trajectory.delta_open_to_close != null && (() => {
                    const delta = trajectory.delta_open_to_close;
                    const trendLabel = Math.abs(delta) < 0.1
                      ? t('assistant.analysis.trendStable')
                      : (delta < -0.2 ? t('assistant.analysis.trendEscalation') : (delta > 0.2 ? t('assistant.analysis.trendDeescalation') : t('assistant.analysis.trendStable')));
                    const trendVariant = Math.abs(delta) < 0.1
                      ? 'gray'
                      : (delta < -0.2 ? 'red' : (delta > 0.2 ? 'emerald' : 'gray'));

                    return (
                      <div className="mt-2.5 flex items-center gap-1.5">
                        <Badge variant={delta > 0 ? 'emerald' : delta < 0 ? 'red' : 'gray'}>
                          {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'} {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                        </Badge>
                        <Badge variant={trendVariant}>{trendLabel}</Badge>
                      </div>
                    );
                  })()}
                  {rationale?.valence_rationale && (
                    <p className="mt-2 text-xs leading-relaxed text-gray-600">{rationale.valence_rationale}</p>
                  )}
                </PanelSection>
              )}

              {(resolvedTurningPoint || chatMetadata?.overall_sentiment) && (
                <PanelSection title={t('assistant.analysis.turningPoint')}>
                  {hasTurningPointDetails ? (
                    <>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {resolvedTurningPoint.turn_id != null && (
                          <Badge variant="gray">{t('assistant.analysis.turnLabel', { id: resolvedTurningPoint.turn_id })}</Badge>
                        )}
                        {resolvedTurningPoint.direction && (
                          <Badge variant={resolvedTurningPoint.direction === 'deescalation' ? 'emerald' : 'red'}>
                            {resolvedTurningPoint.direction}
                          </Badge>
                        )}
                      </div>
                      {resolvedTurningPoint.trigger && (
                        <p className="mt-2 text-xs leading-relaxed text-gray-600">{resolvedTurningPoint.trigger}</p>
                      )}
                      {resolvedTurningPoint.evidence.slice(0, 2).map((evidence, idx) => (
                        <EvidenceQuote key={idx} evidence={evidence} />
                      ))}
                    </>
                  ) : (
                    <p className="text-xs leading-relaxed text-gray-500">{t('assistant.analysis.turningPointEmpty')}</p>
                  )}
                </PanelSection>
              )}

              {displayTags.length > 0 && (
                <PanelSection title={t('assistant.analysis.tags')}>
                  <AnalysisTagList tags={displayTags} t={t} formatLabel={formatTagLabel} />
                </PanelSection>
              )}

              {rationale?.reasoning && !rationale?.intent_fulfilled_rationale && (
                <PanelSection title={t('assistant.analysis.reasoning')}>
                  <p className="text-xs leading-relaxed text-gray-900">{rationale.reasoning}</p>
                  {rationale?.key_emotions?.length > 0 && (
                    <AnalysisTagList
                      className="mt-2"
                      tags={rationale.key_emotions}
                      t={t}
                      formatLabel={(emotion) => emotion.charAt(0).toUpperCase() + emotion.slice(1).toLowerCase()}
                    />
                  )}
                </PanelSection>
              )}
            </>
          ) : (
            <PanelSection>
              <Placeholder
                empty
                size="s"
                message={t('assistant.sessionDetails.noAnalysisAvailable')}
                description={t('assistant.sessionDetails.analysisAutoGenerated')}
              />
            </PanelSection>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={isArchiveModalOpen}
        onClose={() => {
          if (!isArchiveSubmitting) setIsArchiveModalOpen(false);
        }}
        onConfirm={handleArchive}
        title={t('assistant.sessionDetails.archiveSession')}
        description="Are you sure you want to archive this session? You can still find it later in archived records."
        confirmLabel={isArchiveSubmitting ? 'Archiving...' : 'Archive'}
        cancelLabel="Cancel"
        loading={isArchiveSubmitting}
      />
    </>
  );
};

export default ChatDetailsPanel;
